/* eslint-disable import/no-unresolved */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation } from 'react-query';
import { AddPhoneNumberComponent } from 'assets/components';
import { DEFAULT_COUNTRY_CODE, DEFAULT_COUNTRY_CODE_ALPHA2, RECAPTCHA_EVENTS } from '../constants';
import { validatePhoneNumber } from '../../../utilities/formValidation';
import { FLOWS, sendStats } from '../../../services/mixpanel';
import { clearFormError, handleFormBlur } from '../../../utilities/formsUtils';
import {
  MXP_ONBOARDING_SIGN_UP_PHONE_CONFIRM_REQUEST,
  MXP_ONBOARDING_SIGN_UP_PHONE_CONFIRM_VISIT,
} from '../mixpanel-events';
import { showAlert } from '../../../utilities/notifications';
import { PHONE_VERIFICATION_SEND } from '../mutations';
import {
  cacheRecaptchaV3failure,
  clearRecaptchaV3failure,
  resetUserSession,
} from '../../../utilities/caching';
import RecaptchaModal from '../../shared/RecaptchaModal';

const AddPhoneNumber = () => {
  const { t: translate } = useTranslation();

  const { state } = useLocation();
  const history = useHistory();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);

  const [recV2Token, setRecV2Token] = useState('');

  const [form, setForm] = useState({
    country_code: state?.callingCode || DEFAULT_COUNTRY_CODE,
    country_code_alpha2: state?.countryOfResidenceAlpha2 || DEFAULT_COUNTRY_CODE_ALPHA2,
    phone_number: '',
    via: 'sms',
    isSignUp: true,
  });

  useEffect(() => {
    sendStats(MXP_ONBOARDING_SIGN_UP_PHONE_CONFIRM_VISIT, {}, FLOWS.ONBOARDING);
  }, []);

  useEffect(() => {
    if (!state?.email && !state?.partialSignupData?.email) {
      history.push('/onboarding/signup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectDropDownFields = [
    {
      type: 'img',
      data: 'iso3166alpha2',
      class: 'col-1 text-left position-relative',
      img: {
        sourcePath: './images/flags/1x1/',
        ext: '.svg',
        class: 'rounded-circle position-absolute top-50 start-0 translate-middle-y',
      },
    },
    {
      type: 'text',
      data: 'displayName',
      class: 'col-8 ps-2 text-left',
    },
    {
      type: 'text',
      data: 'callingCode',
      class: 'col-3 text-right',
      prefix: '+',
    },
  ];

  const { mutate: requestPhoneVerification, isLoading: isRequestingVerification } = useMutation(
    PHONE_VERIFICATION_SEND,
    {
      onSettled: (data, error) => {
        resetUserSession();
        sendStats(MXP_ONBOARDING_SIGN_UP_PHONE_CONFIRM_REQUEST, { error }, FLOWS.ONBOARDING);
        if (error) {
          if (error?.response?.status === 307) {
            setIsOpenRecV2(true);
            cacheRecaptchaV3failure();
          } else {
            setIsOpenRecV2(false);
            clearRecaptchaV3failure();
            showAlert({ message: error.message, type: 'danger' });
          }
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          history.push(`confirm-phone-number`, {
            ...state,
            ...form,
          });
        }
      },
    }
  );

  const handleChange = (e, fieldName) => {
    if (!e) return;
    if (fieldName === 'country_code') {
      setForm({
        ...form,
        country_code_alpha2: e.iso3166alpha2,
        country_code: e.callingCode,
      });
      return;
    }
    if (!e.target) {
      setForm({ ...form, [fieldName]: e });
    } else {
      const { name, value } = e.target;
      if (name === 'phone_number' && !validatePhoneNumber(value)) {
        return;
      }
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.SIGNUP_VERIFY_PHONE_NUMBER);
      if (!token) {
        return false;
      }

      requestPhoneVerification({ ...form, verificationId: state?.verification_id, token });
    } catch (error) {
      return false;
    }
    return true;
  };

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          form="phone-number-form"
          loading={isRequestingVerification ? 'true' : 'false'}
          disabled={!form.phone_number || !form.country_code}>
          {translate('send_code_button_label')}
        </button>
      </div>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          form="phone-number-form"
          loading={isRequestingVerification ? 'true' : 'false'}
          disabled={!form.phone_number || !form.country_code}>
          {translate('send_code_button_label')}
        </button>
      </div>
    </>
  );

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    requestPhoneVerification({ ...form, verificationId: state?.verification_id, token: tokenV2 });
  };

  return (
    <>
      <AddPhoneNumberComponent
        form={form}
        renderButtons={renderButtons}
        handleChange={handleChange}
        handleFormBlur={handleFormBlur}
        handleSubmitForm={handleSubmitForm}
        selectDropDownFields={selectDropDownFields}
        clearFormError={clearFormError}
      />
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={isRequestingVerification}
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default AddPhoneNumber;
